import { useEffect, useRef } from "react";

const clamp = (n: number) => Math.max(0, Math.min(1, n));

/** OpenMontage-inspired camera/stack choreography, driven by native page scroll.
 * No extra animation dependency, nested scroller, remote script or scroll hijack.
 */
export function useOvaBundleMotion(identity: string) {
  const rootRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const root = rootRef.current;
    const container = root?.querySelector<HTMLElement>(".tw-chapters");
    if (!root || !container) return;
    const cards = Array.from(container.querySelectorAll<HTMLElement>(".tw-chapter"));
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frame = 0;
    let disposed = false;
    const measureAndPaint = () => {
      frame = 0;
      if (disposed) return;
      const pin = parseFloat(getComputedStyle(root).getPropertyValue("--obp-pin")) || 84;
      const fits = cards.every((card) => card.offsetHeight <= window.innerHeight - pin - 20);
      const enabled = !preference.matches && fits;
      root.dataset.motion = enabled ? "on" : "off";
      const top = container.getBoundingClientRect().top;
      let flow = 0;
      cards.forEach((card) => {
        const image = card.querySelector<HTMLImageElement>(".tw-cine-image img");
        const surface = card.querySelector<HTMLElement>(".tw-cine-card");
        const copy = card.querySelector<HTMLElement>(".tw-cine-copy");
        if (!image || !surface || !copy) return;
        const naturalTop = top + flow;
        const step = card.offsetHeight + parseFloat(getComputedStyle(card).marginBottom);
        const camera = clamp((window.innerHeight - naturalTop) / (window.innerHeight + card.offsetHeight));
        const passed = clamp((pin - naturalTop) / step);
        const reveal = clamp((window.innerHeight - naturalTop - window.innerHeight * .12) / (window.innerHeight * .42));
        image.style.transform = enabled ? `translateY(${2 - camera * 4}%) scale(${1.085 - camera * .07})` : "none";
        surface.style.transform = enabled ? `translateY(${-passed * 7}px) scale(${1 - passed * .03})` : "none";
        copy.style.transform = enabled ? `translateY(${(1 - reveal) * 14}px)` : "none";
        copy.style.opacity = enabled ? String(.82 + reveal * .18) : "1";
        flow += step;
      });
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(measureAndPaint); };
    const resize = new ResizeObserver(schedule);
    resize.observe(root);
    cards.forEach((card) => resize.observe(card));
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule, { passive: true });
    preference.addEventListener("change", schedule);
    document.fonts?.ready.then(() => { if (!disposed) schedule(); });
    schedule();
    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      resize.disconnect();
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      preference.removeEventListener("change", schedule);
      delete root.dataset.motion;
    };
  }, [identity]);
  return rootRef;
}
